import { useCallback, useEffect, useRef, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { UploadSimple, Trash, Eye, FilePdf } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const DOC_TYPES = [
  ["BUSTA_PAGA", "Busta paga"], ["HACCP", "Certificato HACCP"], ["FORMAZIONE", "Corso di formazione"],
  ["CONTRATTO", "Contratto"], ["ALTRO", "Altro"],
];

export default function Documents() {
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState("all");
  const [docType, setDocType] = useState("all");
  const [form, setForm] = useState({ employee_id: "", doc_type: "BUSTA_PAGA", title: "", period: "" });
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const fileRef = useRef(null);
  const bulkRef = useRef(null);
  const [bulkPeriod, setBulkPeriod] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [pay, setPay] = useState({ is_owner: false, can_view: false, password_set: false, unlocked: false });
  const [askPwd, setAskPwd] = useState(false);
  const [pwd, setPwd] = useState("");
  const [setPwdOpen, setSetPwdOpen] = useState(false);
  const [pending, setPending] = useState([]);
  const [selezionate, setSelezionate] = useState([]);
  const [assignTo, setAssignTo] = useState({});

  const loadPay = useCallback(() => {
    api.get("/documents/payslip-status").then((r) => setPay(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadPay(); }, [loadPay]);

  const unlock = async () => {
    try {
      await api.post("/documents/payslip-unlock", { password: pwd });
      toast.success("Buste paga sbloccate per 60 minuti");
      setAskPwd(false); setPwd(""); loadPay(); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const savePwd = async () => {
    try {
      await api.post("/documents/payslip-password", { password: pwd });
      toast.success("Password buste paga aggiornata");
      setSetPwdOpen(false); setPwd(""); loadPay();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const importBulk = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBulkBusy(true);
    setBulkResult(null);
    const fd = new FormData();
    fd.append("file", f);
    fd.append("period", bulkPeriod);
    fd.append("doc_type", "BUSTA_PAGA");
    try {
      const { data } = await api.post("/documents/import-payslips", fd);
      setBulkResult(data);
      toast.success(`${data.imported} buste paga archiviate`
        + (data.emails ? ` · ${data.emails} avvisi email in invio` : ""));
      load();
      loadPending();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBulkBusy(false); if (bulkRef.current) bulkRef.current.value = ""; }
  };

  const load = useCallback(() => {
    api.get("/documents", { params: { employee_id: employeeId, doc_type: docType } })
      .then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [employeeId, docType]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/employees").then((r) => setEmployees(r.data)).catch(() => {}); }, []);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    fd.append("employee_id", form.employee_id);
    fd.append("doc_type", form.doc_type);
    fd.append("title", form.title);
    fd.append("period", form.period);
    try {
      const res = await api.post("/documents", fd);
      const d = res.data || {};
      if (d.doc_type === "BUSTA_PAGA" && d.email_sent) toast.success(`Busta paga caricata e inviata a ${d.email_to}`);
      else if (d.doc_type === "BUSTA_PAGA") toast.warning(`Busta paga caricata, avviso email non inviato: ${d.email_error || "errore"}`);
      else toast.success("Documento caricato");
      setForm({ ...form, title: "", period: "" }); load();
    }
    catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const loadPending = useCallback(() => {
    api.get("/documents/payslips/pending").then((r) => setPending(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadPending(); }, [loadPending]);

  const openPending = async (p) => {
    try {
      const res = await api.get(`/documents/payslips/pending/${p.id}/file`, { responseType: "blob" });
      setPreview({ url: URL.createObjectURL(res.data), type: "application/pdf", name: `pagina_${p.page}.pdf` });
    } catch (e) { toast.error(errMsg(e)); }
  };

  const assignPending = async (p) => {
    const employee_id = assignTo[p.id];
    if (!employee_id) { toast.error("Scegli prima il dipendente"); return; }
    try {
      const { data } = await api.post(`/documents/payslips/pending/${p.id}/assign`, { employee_id });
      toast.success(`Busta assegnata a ${data.employee}`
        + (data.email ? ` · avviso email a ${data.email}` : " · nessuna email in anagrafica"));
      loadPending(); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const eliminaSelezionate = async () => {
    if (!window.confirm(`Eliminare ${selezionate.length} buste non assegnate?`)) return;
    try {
      await Promise.all(selezionate.map((id) => api.delete(`/documents/payslips/pending/${id}`)));
      toast.success(`${selezionate.length} pagine eliminate`);
      setSelezionate([]);
      loadPending();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const discardPending = async (p) => {
    if (!window.confirm(`Scartare la pagina ${p.page}${p.period ? ` del periodo ${p.period}` : ""}?`)) return;
    try {
      await api.delete(`/documents/payslips/pending/${p.id}`);
      toast.success("Pagina scartata");
      loadPending();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const open = async (r) => {
    try {
      const res = await api.get(`/documents/${r.id}/file`, { responseType: "blob" });
      setPreview({ url: URL.createObjectURL(res.data), type: r.content_type, name: r.original_filename });
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Eliminare "${r.title}"?`)) return;
    try { await api.delete(`/documents/${r.id}`); toast.success("Documento eliminato"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="documents-page" className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-head font-bold tracking-tight">Documenti Dipendenti</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Buste paga, certificati HACCP, corsi di formazione e contratti. Ogni dipendente vede i propri documenti dal suo account.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {pay.is_owner ? (
            <span className="rounded-sm bg-sky-100 px-2 py-1 text-xs font-semibold text-sky-800"
              data-testid="payslip-owner-note">Puoi caricare buste paga e contratti</span>
          ) : !pay.can_view ? (
            <span className="rounded-sm bg-secondary px-2 py-1 text-xs font-semibold text-muted-foreground"
              data-testid="payslip-no-access">Buste paga riservate all'Amministratore</span>
          ) : pay.unlocked ? (
            <span className="text-xs font-semibold px-2 py-1 rounded-sm bg-emerald-100 text-emerald-800"
              data-testid="payslip-unlocked">Buste paga sbloccate</span>
          ) : (
            <Button variant="outline" size="sm" className="rounded-sm" data-testid="payslip-unlock"
              onClick={() => { setPwd(""); setAskPwd(true); }}>
              SBLOCCA BUSTE PAGA
            </Button>
          )}
        </div>
      </div>

      <Dialog open={askPwd} onOpenChange={setAskPwd}>
        <DialogContent className="rounded-sm sm:max-w-sm" data-testid="payslip-unlock-dialog">
          <DialogHeader><DialogTitle className="font-head">Password buste paga</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Le buste paga sono riservate all'Amministratore: inserisci la password impostata dal Titolare
            nelle Impostazioni.
          </p>
          <Input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && unlock()}
            className="h-10 rounded-sm" placeholder="Password" data-testid="payslip-password-input" />
          <DialogFooter>
            <Button className="rounded-sm" onClick={unlock} data-testid="payslip-unlock-confirm">SBLOCCA</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={setPwdOpen} onOpenChange={setSetPwdOpen}>
        <DialogContent className="rounded-sm sm:max-w-sm" data-testid="payslip-setpwd-dialog">
          <DialogHeader><DialogTitle className="font-head">Password buste paga</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Solo tu (Titolare) puoi impostare o modificare questa password. Serve ai responsabili per vedere
            le buste paga degli altri dipendenti.
          </p>
          <Input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)}
            className="h-10 rounded-sm" placeholder="Nuova password" data-testid="payslip-newpwd-input" />
          <DialogFooter>
            <Button className="rounded-sm" onClick={savePwd} data-testid="payslip-savepwd">SALVA</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="rounded-sm border border-border bg-card p-4 grid md:grid-cols-5 gap-2 items-end">
        <div className="md:col-span-2">
          <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Dipendente</Label>
          <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
            <SelectTrigger className="h-9 rounded-sm mt-1" data-testid="doc-employee"><SelectValue placeholder="Seleziona dipendente" /></SelectTrigger>
            <SelectContent className="max-h-72">
              {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.last_name} {e.first_name} ({e.code})</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Tipo</Label>
          <Select value={form.doc_type} onValueChange={(v) => setForm({ ...form, doc_type: v })}>
            <SelectTrigger className="h-9 rounded-sm mt-1" data-testid="doc-type"><SelectValue /></SelectTrigger>
            <SelectContent>{(pay.is_owner ? DOC_TYPES.filter(([k]) => ["BUSTA_PAGA", "CONTRATTO"].includes(k)) : DOC_TYPES)
              .map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Titolo / Periodo</Label>
          <Input value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })}
            className="h-9 rounded-sm mt-1" placeholder="Es. 08/2026" data-testid="doc-period" />
        </div>
        <div>
          <input ref={fileRef} type="file" accept="image/*,application/pdf" capture="environment" onChange={upload} className="hidden" data-testid="doc-file-input" />
          <Button className="w-full rounded-sm font-semibold" disabled={busy || !form.employee_id}
            onClick={() => fileRef.current?.click()} data-testid="doc-upload-btn">
            <UploadSimple size={16} className="mr-1.5" /> {busy ? "Caricamento..." : "Carica"}
          </Button>
        </div>
      </div>

      <div className="rounded-sm border border-border bg-card p-4">
        <h2 className="font-head font-semibold text-base">Importa buste paga (PDF unico o archivio ZIP)</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Carica il PDF con tutte le buste paga (viene diviso per dipendente riconoscendo codice fiscale o
          nome e cognome) oppure un archivio ZIP con un file per dipendente: l'abbinamento usa il nome del file.
        </p>
        <div className="grid md:grid-cols-3 gap-2 items-end mt-3">
          <div>
            <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Periodo</Label>
            <Input value={bulkPeriod} onChange={(e) => setBulkPeriod(e.target.value)}
              className="h-9 rounded-sm mt-1" placeholder="Es. 07/2026" data-testid="bulk-period" />
          </div>
          <div>
            <input ref={bulkRef} type="file" accept="application/pdf,.zip,application/zip" onChange={importBulk} className="hidden" data-testid="bulk-file-input" />
            <Button variant="outline" className="w-full rounded-sm font-semibold" disabled={bulkBusy || !bulkPeriod.trim()}
              onClick={() => bulkRef.current?.click()} data-testid="bulk-upload-btn">
              <FilePdf size={16} className="mr-1.5" /> {bulkBusy ? "Caricamento in corso..." : "Carica PDF unico o ZIP"}
            </Button>
          </div>
        </div>
        {bulkResult && (
          <div className="mt-3 rounded-sm border border-border bg-secondary p-3 text-xs space-y-1" data-testid="bulk-result">
            <div>
              Pagine totali: <b className="tabular-nums">{bulkResult.total_pages}</b> ·
              Buste archiviate: <b className="tabular-nums text-emerald-700 dark:text-emerald-400">{bulkResult.imported}</b> ·
              Pagine non abbinate: <b className="tabular-nums text-rose-600">{bulkResult.unmatched_pages.length}</b>
            </div>
            {bulkResult.matched.slice(0, 60).map((m, i) => (
              <div key={`${m.code}-${m.pages.join("_")}-${i}`} className="text-muted-foreground">
                {m.employee} ({m.code}){m.pages.length ? ` — pagine ${m.pages.join(", ")}` : ""} · abbinato per {m.matched_by}
              </div>
            ))}
            {bulkResult.unmatched_pages.length > 0 && (
              <div className="text-rose-600" data-testid="bulk-unmatched">
                <div>Pagine da assegnare manualmente: {bulkResult.unmatched_pages.join(", ")}</div>
                <div className="text-[11px]">Le trovi qui sotto in "Buste paga da assegnare".</div>
                {(bulkResult.unmatched_details || []).slice(0, 30).map((u) => (
                  <div key={`pagina-${u.page}`} className="text-[11px]">Pagina {u.page}: {u.reason}</div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {pending.length > 0 && (
        <div className="rounded-sm border border-rose-300 dark:border-rose-900 bg-rose-50/60 dark:bg-rose-950/30 p-3 space-y-2"
          data-testid="pending-payslips">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-head font-semibold text-base md:text-lg">Buste paga da assegnare</h2>
            <span className="text-xs font-semibold px-2 py-0.5 rounded-sm bg-rose-500/15 text-rose-700 dark:text-rose-300 tabular-nums"
              data-testid="pending-count">{pending.length}</span>
            <span className="text-xs text-muted-foreground">
              Apri la pagina per leggere il nominativo, scegli il dipendente e assegnala.
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-3 border-t border-rose-300/60 dark:border-rose-900/60 pt-2">
            <label className="flex items-center gap-2 text-sm font-semibold">
              <input type="checkbox" data-testid="pending-select-all"
                checked={pending.length > 0 && selezionate.length === pending.length}
                onChange={(e) => setSelezionate(e.target.checked ? pending.map((p) => p.id) : [])} />
              Seleziona tutto
            </label>
            {selezionate.length > 0 && (
              <>
                <span className="text-xs text-muted-foreground tabular-nums" data-testid="pending-selected-count">
                  {selezionate.length} selezionate
                </span>
                <Button size="sm" className="h-8 rounded-sm bg-rose-600 font-semibold hover:bg-rose-700"
                  data-testid="pending-delete-selected" onClick={eliminaSelezionate}>
                  <Trash size={14} className="mr-1.5" /> Elimina selezionate
                </Button>
              </>
            )}
          </div>
          <div className="space-y-2">
            {pending.map((p) => (
              <div key={p.id} className="flex flex-wrap items-center gap-2 rounded-sm border border-border bg-card p-2"
                data-testid={`pending-row-${p.id}`}>
                <input type="checkbox" data-testid={`pending-check-${p.id}`}
                  checked={selezionate.includes(p.id)}
                  onChange={(e) => setSelezionate(e.target.checked
                    ? [...selezionate, p.id] : selezionate.filter((x) => x !== p.id))} />
                <div className="min-w-[180px]">
                  <div className="text-sm font-semibold">Pagina {p.page}{p.period ? ` · ${p.period}` : ""}</div>
                  <div className="text-[11px] text-muted-foreground">{p.reason || "non abbinata"}</div>
                </div>
                <Button variant="outline" size="sm" className="h-8 rounded-sm" data-testid={`pending-view-${p.id}`}
                  onClick={() => openPending(p)}>
                  <Eye size={14} className="mr-1.5" /> Apri PDF
                </Button>
                <Select value={assignTo[p.id] || ""}
                  onValueChange={(v) => setAssignTo({ ...assignTo, [p.id]: v })}>
                  <SelectTrigger className="h-8 w-[260px] rounded-sm" data-testid={`pending-employee-${p.id}`}>
                    <SelectValue placeholder="Scegli il dipendente" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {employees.map((e) => (
                      <SelectItem key={e.id} value={e.id}>{e.last_name} {e.first_name} ({e.code})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button size="sm" className="h-8 rounded-sm font-semibold" data-testid={`pending-assign-${p.id}`}
                  disabled={!assignTo[p.id]} onClick={() => assignPending(p)}>
                  Assegna
                </Button>
                <button type="button" className="text-rose-600 hover:text-rose-700 p-1 ml-auto"
                  title="Scarta la pagina" data-testid={`pending-discard-${p.id}`} onClick={() => discardPending(p)}>
                  <Trash size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-sm border border-border bg-card p-3 flex flex-wrap gap-2">
        <Select value={employeeId} onValueChange={setEmployeeId}>
          <SelectTrigger className="h-9 w-[240px] rounded-sm" data-testid="doc-filter-employee"><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value="all">Tutti i dipendenti</SelectItem>
            {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.last_name} {e.first_name} ({e.code})</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={docType} onValueChange={setDocType}>
          <SelectTrigger className="h-9 w-[200px] rounded-sm" data-testid="doc-filter-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i tipi</SelectItem>
            {DOC_TYPES.map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-sm border border-border bg-card overflow-x-auto scroll-thin">
        <table className="w-full text-sm tbl-sticky" data-testid="documents-table">
          <thead className="bg-secondary">
            <tr>{["", "Dipendente", "Tipo", "Titolo", "Periodo", "Caricato il", "File"].map((h) => (
              <th key={h} className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap">{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-border hover:bg-secondary/40" data-testid={`doc-row-${r.id}`}>
                <td className="px-3 py-1.5">
                  <div className="flex justify-start">
                    <Button variant="ghost" size="icon" className="h-7 w-7 rounded-sm text-destructive" onClick={() => remove(r)} data-testid={`doc-delete-${r.id}`}>
                      <Trash size={15} />
                    </Button>
                  </div>
                </td>
                <td className="px-3 py-1.5">
                  <div className="font-semibold">{r.employee_name}</div>
                  <div className="text-[10px] text-muted-foreground tabular-nums">{r.employee_code}</div>
                </td>
                <td className="px-3 py-1.5 text-xs">{(DOC_TYPES.find(([k]) => k === r.doc_type) || [])[1] || r.doc_type}</td>
                <td className="px-3 py-1.5">{r.title}</td>
                <td className="px-3 py-1.5 tabular-nums text-muted-foreground">{r.period || "—"}</td>
                <td className="px-3 py-1.5 tabular-nums text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString("it-IT")}</td>
                <td className="px-3 py-1.5">
                  <button className="text-primary hover:underline text-xs flex items-center gap-1" onClick={() => open(r)} data-testid={`doc-open-${r.id}`}>
                    <Eye size={14} /> Apri
                  </button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} className="px-3 py-8 text-center text-muted-foreground" data-testid="documents-empty">Nessun documento</td></tr>}
          </tbody>
        </table>
      </div>

      {preview && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setPreview(null)} data-testid="doc-preview">
          <div className="bg-card rounded-sm border border-border max-w-3xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-border px-4 py-2">
              <span className="text-sm font-semibold">{preview.name}</span>
              <a href={preview.url} download={preview.name} className="text-xs text-primary hover:underline">Scarica</a>
            </div>
            {preview.type === "application/pdf"
              ? <iframe title="documento" src={preview.url} className="w-full h-[75vh]" />
              : <img src={preview.url} alt="documento" className="w-full object-contain" />}
          </div>
        </div>
      )}
    </div>
  );
}
